import { FC, useContext, useEffect, useState } from 'react';
import { format, parseISO, set } from 'date-fns';
import { LoadingTable } from '../../Core/Components/LoadingTable/LoadingTable';
import {
	Location,
	StoreOrder,
	LineItem,
	Tab,
} from '../../Core/SharedTypes/Models';
import { useGetMyLocations } from '../../Core/Hooks/LocationHooks';
import { usePrepareLineItem } from '../../Core/Hooks/OrderBookHooks';

import './ProductionOrdersTable.scss';
import { Tabs } from '../../Core/Components/Tabs/Tabs';
import { NullScreen } from '../../Core/Components/NullScreen/NullScreen';
import { Lightswitch } from '../../Core/Components/Lightswitch/Lightswitch';
import { generateTitle } from '../../Core/Utils/GenerateTitles';
import { DateContext } from '../../Core/Contexts/DateContext';
import { Pages } from '../../Core/Enums';

export type Props = {
	currentLocation: Location | undefined;
	orders: StoreOrder[] | undefined;
	isLoading: boolean;
	isFetching?: boolean;
};

export const ProductionOrdersTable: FC<Props> = ({
	currentLocation,
	orders,
	isLoading,
	isFetching,
}) => {
	const page = Pages.ORDERBOOK_STORE_PAGE;
	const [selectedDate] = useContext(DateContext);
	const printTitle = generateTitle(selectedDate[0], page);

	const { data: locations } = useGetMyLocations();
	const [selectedTab, setSelectedTab] = useState<number>(0);
	const tabItems: Tab[] = [
		{
			title: 'Bageri',
			key: 0,
		},
		{
			title: 'Konditori',
			key: 1,
		},
	];

	const [localOrders, setLocalOrders] = useState<StoreOrder[] | undefined>(
		orders,
	);
	useEffect(() => {
		setLocalOrders(orders);
	}, [orders]);

	useEffect(() => {
		if (currentLocation && currentLocation.title === 'Konditori') {
			setSelectedTab(1);
		}
	}, [currentLocation]);

	const handleTabChange = (tab: number) => {
		setSelectedTab(tab);
	};

	// First, filter the locations outside the JSX
	const locationsWithPlannedOrders = locations
		? locations.filter((location) => {
				// Check if there are any orders for this location
				let locationOrders: StoreOrder[] = localOrders
					? localOrders.filter(
							(order) => order && order.locationId == location.id,
						)
					: [];

				if (locationOrders.length === 0) {
					return false; // No orders for this location
				}

				// Check if any order has at least one line item with planning value of 1
				return locationOrders.some((order) => {
					// Make sure order and lineItems exist
					if (!order || !order.lineItems || !order.lineItems.length) {
						return false;
					}

					// Check based on selected tab
					return order.lineItems.some((lineItem) => {
						if (selectedTab == 0) {
							return lineItem.plannedInBakery == 1;
						} else {
							return lineItem.plannedInPastry == 1;
						}
					});
				});
			})
		: [];

	// Filter line items based on selected tab
	const filterLineItemsByTab = (lineItems: LineItem[] | undefined) => {
		if (!lineItems) return [];

		return lineItems.filter((lineItem) => {
			if (selectedTab == 0) {
				return lineItem.plannedInBakery == 1;
			} else {
				return lineItem.plannedInPastry == 1;
			}
		});
	};

	const { prepareLineItem, isPreparing } = usePrepareLineItem();
	const togglePrepared = async (lineItem: LineItem, newValue: boolean) => {
		setLocalOrders((prevOrders) => {
			if (!prevOrders) return undefined;
			return prevOrders.map((order) => {
				if (!order.lineItems?.find((i) => i.id === lineItem.id)) return order;

				return {
					...order,
					lineItems: (order.lineItems || []).map((item) => {
						if (item.id === lineItem.id) {
							return {
								...item,
								preparedBakery:
									selectedTab === 0 ? (newValue ? 1 : 0) : item.preparedBakery,
								preparedPastry:
									selectedTab === 1 ? (newValue ? 1 : 0) : item.preparedPastry,
							};
						}
						return item;
					}),
				};
			});
		});

		try {
			const updatedItem = { ...lineItem };
			if (selectedTab === 0) {
				updatedItem.preparedBakery = newValue ? 1 : 0;
			} else {
				updatedItem.preparedPastry = newValue ? 1 : 0;
			}

			await prepareLineItem(updatedItem);
		} catch (error) {
			console.error('Failed to update line item', error);
			alert('Failed to save change!');
		}
	};

	return (
		<>
			<Tabs
				selectedTab={selectedTab}
				items={tabItems}
				onChange={(newTab) => {
					handleTabChange(newTab);
				}}
			/>

			<div className="production-orders-table">
				{/* LOADING */}
				{(isFetching || isLoading) && <LoadingTable />}

				{printTitle && (
					<div className="print-only-footer">
						<h1 className="print-only-footer__date">{printTitle}</h1>
						<h1 className="print-only-footer__location">
							{currentLocation?.title}
						</h1>
					</div>
				)}

				{/* LOCATION */}
				{!isFetching &&
				!isLoading &&
				locationsWithPlannedOrders &&
				locationsWithPlannedOrders.length > 0 ? (
					locationsWithPlannedOrders.map(({ id, title }) => {
						return (
							<div className="production-orders-table__locations" key={title}>
								{title}
								{/* HEADER */}
								<div className="production-orders-table__header">
									<div className="header-title">Afhentning</div>
									<div className="header-title">Ordrenummer</div>
									<div className="header-title">Antal</div>
									<div className="header-title">Bestilling</div>
									<div className="header-title">Klargjort</div>
								</div>

								{/* ORDER*/}
								{!isFetching &&
									!isLoading &&
									localOrders &&
									localOrders.map((order: StoreOrder, orderIndex: number) => {
										const visibleLineItems = filterLineItemsByTab(
											order.lineItems,
										);

										if (
											!order ||
											order.locationId !== id ||
											visibleLineItems.length === 0
										) {
											return null;
										}

										return (
											<div
												key={order.id}
												className="production-orders-table__orders"
											>
												{/* LINEITEM */}
												<div className="production-orders-table__lineitems">
													{filterLineItemsByTab(order.lineItems).map(
														(lineItem: LineItem) => {
															const isPrepared =
																selectedTab === 0
																	? lineItem.preparedBakery === 1
																	: lineItem.preparedPastry === 1;

															return (
																<div
																	key={lineItem.id}
																	className={
																		lineItem.approved == 1
																			? 'lineitem lineitem--approved'
																			: 'lineitem'
																	}
																>
																	<div className="lineitem__column lineitem__date-time">
																		<span className="time">
																			{format(
																				parseISO(order.pickupTime),
																				'HH.mm',
																			)}
																		</span>
																	</div>

																	<div className="lineitem__column lineitem__ordernumber">
																		{order.orderNumber}
																	</div>

																	<div className="lineitem__column lineitem__qty">
																		{lineItem.qty}
																	</div>

																	<div className="lineitem__column lineitem__description">
																		{lineItem.description}
																	</div>
																	<div className="lineitem__column lineitem__prepared">
																		<div className="flex items-center gap-2">
																			<Lightswitch
																				text=""
																				value={isPrepared}
																				onToggle={(newValue) =>
																					togglePrepared(lineItem, newValue)
																				}
																			/>
																			<span className="lineitem__prepared--status">
																				{isPrepared
																					? 'Klargjort'
																					: 'Ikke klargjort'}
																			</span>
																		</div>
																	</div>
																</div>
															);
														},
													)}
												</div>
											</div>
										);
									})}
							</div>
						);
					})
				) : !isLoading && !isFetching ? (
					<NullScreen text="Ingen bestillinger til planlægning" />
				) : null}
			</div>
		</>
	);
};
